"use client";

import { Copy, Send2, Share } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface BlogShareRowProps {
  url: string;
  title: string;
  className?: string;
}

const BTN =
  "grid size-10 place-items-center rounded-lg text-primary transition-colors hover:bg-bg-tertiary";

/**
 * Detail-page share row: label on the reading-start (right in RTL), share targets
 * on the left. Only targets that can actually share a *link* on the web ship —
 * Telegram (deterministic share intent) + copy-link, plus the OS native-share
 * sheet when the platform supports it (that's the path to Instagram et al. on
 * mobile; Instagram has no web link-share scheme, so no dedicated button).
 */
export const BlogShareRow: React.FC<BlogShareRowProps> = ({
  url,
  title,
  className,
}) => {
  const t = useTranslations("blog");
  const [copied, setCopied] = useState(false);
  // Feature-detect AFTER mount so SSR and the first client render agree (no
  // hydration mismatch). navigator.share is undefined on the server and on most
  // desktop browsers.
  const [canShare, setCanShare] = useState(false);
  useEffect(() => {
    setCanShare(
      typeof navigator !== "undefined" && typeof navigator.share === "function",
    );
  }, []);

  const telegramHref = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;

  const copy = () => {
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const nativeShare = () => {
    navigator.share?.({ title, url });
  };

  return (
    <div className={cn("flex items-center justify-between", className)}>
      <span className="text-sm text-text-secondary">{t("share.label")}</span>
      <div className="flex items-center gap-2">
        {copied && (
          <span className="text-xs text-semantic-success">
            {t("share.copied")}
          </span>
        )}
        <button
          type="button"
          aria-label={t("share.ariaLinkCopy")}
          onClick={copy}
          className={BTN}
        >
          <Copy size={20} />
        </button>
        <a
          aria-label={t("share.ariaTelegram")}
          href={telegramHref}
          target="_blank"
          rel="noreferrer"
          className={BTN}
        >
          <Send2 size={20} />
        </a>
        {canShare && (
          <button
            type="button"
            aria-label={t("share.ariaNativeShare")}
            onClick={nativeShare}
            className={BTN}
          >
            <Share size={20} />
          </button>
        )}
      </div>
    </div>
  );
};
